import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, Sparkles, MessageSquare, Mic, MicOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTER_QUESTIONS = [
  "Tell me about your services",
  "Tell me about UniDAF AI Platform",
  "Government project experience",
  "Schedule a consultation",
];

function loadSessionId() {
  try {
    return localStorage.getItem("gesil_chat_session") || null;
  } catch {
    return null;
  }
}

function saveSessionId(id) {
  try {
    localStorage.setItem("gesil_chat_session", id);
  } catch {
    /* ignore */
  }
}

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

/** Tiny markdown renderer (bold, paragraphs, bullets). */
function renderMarkdown(text) {
  const lines = (text || "").split(/\r?\n/);
  const blocks = [];
  let bulletBuf = [];
  const flushBullets = () => {
    if (!bulletBuf.length) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1 my-2">
        {bulletBuf.map((b, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: applyInline(b) }} />
        ))}
      </ul>
    );
    bulletBuf = [];
  };
  const applyInline = (line) =>
    line
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-[#1D4ED8]">$1</strong>');
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^[-*]\s+/.test(line)) {
      bulletBuf.push(line.replace(/^[-*]\s+/, ""));
    } else if (!line.trim()) {
      flushBullets();
      blocks.push(<div key={`sp-${i}`} className="h-2" />);
    } else {
      flushBullets();
      blocks.push(
        <p
          key={`p-${i}`}
          className="leading-relaxed"
          dangerouslySetInnerHTML={{ __html: applyInline(line) }}
        />
      );
    }
  });
  flushBullets();
  return blocks;
}

export default function ChatbotMascot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hint, setHint] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [peek, setPeek] = useState(false);
  const [emotion, setEmotion] = useState("idle"); // idle | thinking | happy | greeting
  const [listening, setListening] = useState(false);
  const [sessionId, setSessionId] = useState(loadSessionId);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);

  const greeting = useMemo(() => getTimeGreeting(), []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, sending, open]);

  // Pulse every 10s + show hint briefly the first 8s
  useEffect(() => {
    if (open) return;
    setHint(true);
    const hideHint = setTimeout(() => setHint(false), 8000);
    const pulseInt = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 1600);
    }, 10000);
    return () => {
      clearTimeout(hideHint);
      clearInterval(pulseInt);
    };
  }, [open]);

  // Welcome message + greeting animation when opened first time
  useEffect(() => {
    if (open && messages.length === 0) {
      setEmotion("greeting");
      const welcome = `${greeting}! I'm **TechBuddy AI**, your Technology Expert from GESIL.

I can help you with software solutions, digital transformation, cloud services, AI, government projects and technical consultations. How may I assist you today?`;
      setMessages([{ role: "assistant", content: welcome, isWelcome: true }]);
      setTimeout(() => setEmotion("idle"), 3000);
    }
  }, [open, messages.length, greeting]);

  const sendMessage = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setSending(true);
    setEmotion("thinking");
    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message,
      });
      const { reply, session_id: sid, lead_captured } = res.data || {};
      if (sid && sid !== sessionId) {
        setSessionId(sid);
        saveSessionId(sid);
      }
      setMessages((m) => [
        ...m,
        { role: "assistant", content: reply, leadCaptured: !!lead_captured },
      ]);
      setEmotion(lead_captured ? "happy" : "idle");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            typeof detail === "string"
              ? detail
              : "I'm having trouble reaching the GESIL servers right now. Please try again in a moment, or email info@gesil.co.in.",
        },
      ]);
      setEmotion("idle");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Voice input via Web Speech API (browser-native, no extra cost)
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Voice input is not supported in this browser. Try Chrome or Edge.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop?.();
      return;
    }
    const r = new SR();
    r.lang = "en-IN";
    r.interimResults = true;
    r.continuous = false;
    r.onstart = () => setListening(true);
    r.onresult = (e) => {
      let transcript = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setInput(transcript);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    r.start();
  };

  return (
    <>
      {/* Floating mascot button */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.7, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 40 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="fixed right-5 bottom-5 md:right-7 md:bottom-7 z-[60] flex items-end gap-3"
            onMouseEnter={() => setPeek(true)}
            onMouseLeave={() => setPeek(false)}
          >
            {/* Hover/initial speech bubble */}
            <AnimatePresence>
              {(hint || peek) && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.9 }}
                  transition={{ duration: 0.25 }}
                  className="hidden sm:flex flex-col gap-1 items-start max-w-[230px] bg-white border border-blue-100 rounded-2xl rounded-br-sm px-3.5 py-2.5 shadow-[0_14px_36px_-14px_rgba(37,99,235,0.4)]"
                  data-testid="chatbot-hint"
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#2563EB] inline-flex items-center gap-1">
                    <Sparkles size={11} /> Need Technical Assistance?
                  </div>
                  <div className="text-[12.5px] text-[#0F172A] leading-snug">
                    {peek ? (
                      <>
                        Hi! I&apos;m <strong>TechBuddy AI</strong>, your Tech Expert. How can I help you today?
                      </>
                    ) : (
                      <>
                        Hi! I&apos;m <strong>TechBuddy AI</strong>. Tap me to chat.
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="button"
              onClick={() => {
                setOpen(true);
                setHint(false);
                setPeek(false);
              }}
              data-testid="chatbot-mascot-open"
              aria-label="Open TechBuddy AI chatbot"
              className="relative h-[72px] w-[72px] md:h-[80px] md:w-[80px] rounded-full focus:outline-none focus:ring-4 focus:ring-blue-300/60"
            >
              {/* Pulse rings */}
              <span
                className={`absolute inset-0 rounded-full bg-[#2563EB] opacity-30 ${
                  pulse ? "animate-ping" : ""
                }`}
              />
              <span className="absolute inset-[5px] rounded-full bg-gradient-to-br from-[#60A5FA] to-[#1E3A8A] blur-md opacity-60" />
              <TechBuddyAvatar emotion={peek ? "greeting" : "idle"} size={72} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat popup */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 60, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            data-testid="chatbot-popup"
            className="fixed right-4 bottom-4 md:right-7 md:bottom-7 z-[60] w-[min(400px,calc(100vw-2rem))] h-[min(640px,calc(100vh-5rem))] flex flex-col bg-white/95 backdrop-blur-2xl rounded-3xl shadow-[0_40px_120px_-30px_rgba(37,99,235,0.55)] border border-white/60 ring-1 ring-blue-100 overflow-hidden"
          >
            {/* Glass header */}
            <div className="relative px-5 pt-5 pb-4 bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E3A8A] text-white">
              <div className="absolute inset-0 bg-grid-blue opacity-15" />
              <div className="absolute -bottom-6 left-5 h-12 w-12 rounded-2xl bg-white/10 backdrop-blur-md ring-1 ring-white/30 flex items-center justify-center overflow-hidden">
                <TechBuddyAvatar emotion={emotion} size={44} />
              </div>
              <div className="relative flex items-start justify-between">
                <div className="ml-16">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-blue-200">
                    Technology Expert
                  </div>
                  <div className="font-display text-lg leading-tight">TechBuddy AI</div>
                  <div className="text-[11px] text-blue-100/90 mt-0.5 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Online · Powered by Claude
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="chatbot-close"
                  aria-label="Close chatbot"
                  className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="chatbot-messages"
              className="flex-1 overflow-y-auto p-4 pt-7 space-y-3 bg-gradient-to-b from-blue-50/40 to-white"
            >
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={`chatbot-msg-${m.role}-${i}`}
                    className={`max-w-[85%] text-[13.5px] px-3.5 py-2.5 rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#2563EB] text-white rounded-br-sm"
                        : "bg-white border border-blue-100 text-[#0F172A] rounded-bl-sm shadow-sm"
                    }`}
                  >
                    {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                    {m.role === "assistant" && m.leadCaptured && (
                      <div className="mt-3 -mx-1 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Lead captured — GESIL team notified
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Quick reply suggestions (under welcome msg only) */}
              {messages.length === 1 && messages[0].isWelcome && (
                <div className="grid gap-2 pt-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">
                    Quick questions
                  </div>
                  {STARTER_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => sendMessage(q)}
                      data-testid={`chatbot-starter-${q.slice(0, 8)}`}
                      className="text-left text-[13px] bg-white border border-blue-100 hover:border-[#2563EB] hover:bg-blue-50 rounded-xl px-3 py-2.5 transition-colors text-[#0F172A]"
                    >
                      <MessageSquare size={12} className="inline mr-2 text-[#2563EB]" />
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white border border-blue-100 rounded-2xl rounded-bl-sm px-3.5 py-3 shadow-sm">
                    <div className="flex gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB] animate-bounce" />
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-[#2563EB] animate-bounce"
                        style={{ animationDelay: "0.15s" }}
                      />
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-[#2563EB] animate-bounce"
                        style={{ animationDelay: "0.3s" }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="p-3 border-t border-blue-100 bg-white/70 backdrop-blur">
              <div className="relative">
                <textarea
                  data-testid="chatbot-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={listening ? "Listening…" : "Ask about GESIL services, AI, projects…"}
                  rows={1}
                  className="w-full resize-none rounded-xl border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm px-3.5 py-2.5 pr-20 text-[#0F172A] placeholder:text-[#94A3B8]"
                />
                <button
                  type="button"
                  onClick={startVoice}
                  data-testid="chatbot-voice"
                  aria-label="Voice input"
                  className={`absolute right-12 bottom-1.5 h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${
                    listening
                      ? "bg-red-500 text-white animate-pulse"
                      : "bg-blue-50 hover:bg-blue-100 text-[#1D4ED8]"
                  }`}
                  title={listening ? "Stop listening" : "Speak to type"}
                >
                  {listening ? <MicOff size={14} /> : <Mic size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || sending}
                  data-testid="chatbot-send"
                  aria-label="Send message"
                  className="absolute right-1.5 bottom-1.5 h-8 w-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:from-[#1D4ED8] hover:to-[#1E40AF] transition-colors"
                >
                  <Send size={14} />
                </button>
              </div>
              <div className="mt-1.5 text-[10px] text-[#94A3B8] text-center">
                TechBuddy AI · Trained on GESIL · Voice + Text supported
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * TechBuddyAvatar — a 2D animated humanoid Tech Expert mascot rendered as pure SVG.
 * Includes glasses, headset, friendly face, subtle breathing motion and emotion
 * variants for idle / thinking / happy / greeting (used in the chat header).
 */
function TechBuddyAvatar({ emotion = "idle", size = 56 }) {
  const isThinking = emotion === "thinking";
  const isHappy = emotion === "happy";
  const isGreeting = emotion === "greeting";

  return (
    <span
      className="relative inline-flex items-center justify-center mascot-float"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" width={size} height={size}>
        <defs>
          <radialGradient id="tb-bg" cx="50%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#DBEAFE" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="tb-skin" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FDE2C7" />
            <stop offset="100%" stopColor="#F4C49C" />
          </linearGradient>
          <linearGradient id="tb-suit" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1D4ED8" />
            <stop offset="100%" stopColor="#0B1B40" />
          </linearGradient>
          <linearGradient id="tb-shirt" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E0F2FE" />
            <stop offset="100%" stopColor="#BAE6FD" />
          </linearGradient>
        </defs>

        {/* halo */}
        <circle cx="50" cy="50" r="48" fill="url(#tb-bg)" />

        {/* suit / shoulders */}
        <path
          d="M14 96 C 14 76, 28 66, 50 66 C 72 66, 86 76, 86 96 L 14 96 Z"
          fill="url(#tb-suit)"
        />
        {/* shirt + collar */}
        <path d="M40 66 L50 78 L60 66 L57 86 L43 86 Z" fill="url(#tb-shirt)" />
        <path d="M50 78 L48 86 L52 86 Z" fill="#1D4ED8" />

        {/* neck */}
        <rect x="44" y="56" width="12" height="12" rx="3" fill="url(#tb-skin)" />

        {/* head */}
        <ellipse cx="50" cy="40" rx="20" ry="22" fill="url(#tb-skin)" />

        {/* hair */}
        <path
          d="M30 38 C 30 22, 42 16, 50 16 C 60 16, 70 22, 70 36 L 70 40 C 64 32, 56 30, 50 30 C 42 30, 36 34, 30 40 Z"
          fill="#1F2937"
        />

        {/* headset arc */}
        <path
          d="M30 30 C 30 18, 50 14, 70 22"
          stroke="#0F172A"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
        />
        {/* ear cup */}
        <rect x="26" y="32" width="6" height="10" rx="2.5" fill="#1D4ED8" />
        <circle cx="29" cy="37" r="1.2" fill="#60FAE0">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" />
        </circle>
        {/* mic boom */}
        <path d="M30 42 C 28 50, 38 56, 44 55" stroke="#0F172A" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        <circle cx="44" cy="55" r="1.8" fill="#1D4ED8" />

        {/* glasses */}
        <g className={isThinking ? "tb-glasses-think" : ""}>
          <rect x="35" y="38" width="12" height="9" rx="3" fill="#fff" fillOpacity="0.15" stroke="#0F172A" strokeWidth="1.6" />
          <rect x="53" y="38" width="12" height="9" rx="3" fill="#fff" fillOpacity="0.15" stroke="#0F172A" strokeWidth="1.6" />
          <line x1="47" y1="42" x2="53" y2="42" stroke="#0F172A" strokeWidth="1.4" />
          {/* lens shine */}
          <line x1="37" y1="40" x2="40" y2="40" stroke="#fff" strokeWidth="1" opacity="0.8" />
          <line x1="55" y1="40" x2="58" y2="40" stroke="#fff" strokeWidth="1" opacity="0.8" />
        </g>

        {/* eyes (blink + look around when thinking) */}
        <g className="mascot-eyes">
          <circle cx="41" cy="43" r="1.4" fill="#0F172A">
            {isThinking && (
              <animate attributeName="cx" values="41;43;39;41" dur="1.6s" repeatCount="indefinite" />
            )}
          </circle>
          <circle cx="59" cy="43" r="1.4" fill="#0F172A">
            {isThinking && (
              <animate attributeName="cx" values="59;61;57;59" dur="1.6s" repeatCount="indefinite" />
            )}
          </circle>
        </g>

        {/* eyebrows */}
        <path d={isHappy ? "M37 35 Q41 33 45 35" : "M37 36 L45 35"} stroke="#0F172A" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d={isHappy ? "M55 35 Q59 33 63 35" : "M55 35 L63 36"} stroke="#0F172A" strokeWidth="1.5" fill="none" strokeLinecap="round" />

        {/* mouth */}
        {isHappy || isGreeting ? (
          <path d="M43 53 Q50 58 57 53" stroke="#0F172A" strokeWidth="2" fill="none" strokeLinecap="round" />
        ) : isThinking ? (
          <path d="M44 54 L56 54" stroke="#0F172A" strokeWidth="2" strokeLinecap="round" />
        ) : (
          <path d="M44 53 Q50 56 56 53" stroke="#0F172A" strokeWidth="2" fill="none" strokeLinecap="round" />
        )}

        {/* thinking bubble */}
        {isThinking && (
          <g>
            <circle cx="80" cy="22" r="3" fill="#fff" stroke="#2563EB" strokeWidth="1.2">
              <animate attributeName="cy" values="22;18;22" dur="1.6s" repeatCount="indefinite" />
            </circle>
            <circle cx="86" cy="14" r="4.5" fill="#fff" stroke="#2563EB" strokeWidth="1.2">
              <animate attributeName="cy" values="14;10;14" dur="1.6s" begin="0.2s" repeatCount="indefinite" />
            </circle>
            <text x="86" y="17" textAnchor="middle" fontSize="6" fill="#2563EB" fontWeight="700">AI</text>
          </g>
        )}

        {/* greeting wave: little hand */}
        {isGreeting && (
          <g className="tb-wave">
            <ellipse cx="78" cy="74" rx="5" ry="6" fill="url(#tb-skin)" />
            <path d="M74 70 L82 78" stroke="#F4C49C" strokeWidth="0" />
          </g>
        )}

        {/* AI badge pin on collar */}
        <circle cx="58" cy="84" r="3" fill="#60FAE0" />
        <text x="58" y="86" textAnchor="middle" fontSize="3.5" fontWeight="700" fill="#0B1B40">AI</text>
      </svg>
    </span>
  );
}
